import EventEmitter from 'eventemitter3';
import ISubmitFormData from '../data/ISubmitFormData';
import SubmitFormEvents from '../events/SubmitFormEvents';
import { getMovieClipByName, getTextByName, MovieClip, Stage } from '../utils/utils';
import StatusText from './StatusText';

export default class SubmitForm {
  // ==============================================================================================
  // properties
  // ==============================================================================================
  private _events: EventEmitter;

  private txtNameStatus: StatusText;

  private txtEmailStatus: StatusText;

  private txtSubmitStatus: StatusText;

  private form: MovieClip;

  private dimBack: MovieClip;

  private panel: MovieClip;

  private closeButton: MovieClip;

  private submitButton: MovieClip;

  private nameInput: HTMLInputElement;

  private emailInput: HTMLInputElement;

  private isInit: boolean;

  // ==============================================================================================
  // public
  // ==============================================================================================

  // ----------------------------------------------------------------------------------------------
  constructor(stage: Stage) {
    this._events = new EventEmitter();
    this.form = getMovieClipByName(stage, 'submit_form');
  }

  // ----------------------------------------------------------------------------------------------
  set enabled(value: boolean) {
    this.nameInput.disabled = !value;
    this.emailInput.disabled = !value;
    this.closeButton.mouseEnabled = value;
    this.submitButton.mouseEnabled = value;
    this.dimBack.mouseEnabled = value;
  }

  // ----------------------------------------------------------------------------------------------
  get events(): EventEmitter {
    return (this._events);
  }

  // ----------------------------------------------------------------------------------------------
  present(): void {
    if (!this.form) {
      return;
    }

    this.init()
      .then(() => {
        this.form.visible = true;
        this.enabled = true;
      });
  }

  // ----------------------------------------------------------------------------------------------
  get statusText(): StatusText {
    return (this.txtSubmitStatus);
  }

  // ----------------------------------------------------------------------------------------------
  unpresent(): void {
    if (this.form) {
      this.form.visible = false;
    }
  }

  // ==============================================================================================
  // private
  // ==============================================================================================

  // ----------------------------------------------------------------------------------------------
  private init(): Promise<void> {
    // bring this form to the top
    this.form.parent.setChildIndex(this.form, this.form.parent.numChildren - 1);

    this.form.visible = true;

    const promise = new Promise<void>((resolve) => {
      if (this.isInit) {
        resolve();
      } else {
        createjs.Ticker.on('tick', () => {
          this.init2();
          resolve();
        }, this, true);
      }
    });

    this.isInit = true;

    return (promise);
  }

  // ----------------------------------------------------------------------------------------------
  private init2(): void {
    this.nameInput = SubmitForm.InitInputElement('txt_name', 'First Name');

    this.emailInput = SubmitForm.InitInputElement('txt_email', 'Email');
    this.emailInput.type = 'email';

    this.dimBack = getMovieClipByName(this.form, 'dim_back');
    this.dimBack.on('mousedown', () => {
      this.unpresent();
    });

    this.panel = getMovieClipByName(this.form, 'panel');
    this.panel.on('mousedown', () => {
      // Pressing on the panel should not pass thru to the dimmed back.
      // Just need something here (a "no-op" would've been ideal) to prevent click-thru
      // to the dimmed back.
      this.panel.visible = true;
    });

    this.txtNameStatus = new StatusText(getTextByName(this.panel, 'txt_name_status'));
    this.txtEmailStatus = new StatusText(getTextByName(this.panel, 'txt_email_status'));

    this.txtSubmitStatus = new StatusText(getTextByName(this.panel, 'txt_status'));

    this.closeButton = getMovieClipByName(this.panel, 'btn_close');
    this.closeButton.on('click', () => {
      this.unpresent();
    });

    this.submitButton = getMovieClipByName(this.panel, 'btn_submit');
    this.submitButton.on('click', () => {
      this.submit();
    });
  }

  // ----------------------------------------------------------------------------------------------
  private static InitInputElement(id: string, placeholder: string): HTMLInputElement {
    const element = <HTMLInputElement> document.getElementById(id);
    element.placeholder = placeholder;
    element.required = true;
    element.style.borderColor = 'rgba(42, 42, 42, 0.5)';
    element.style.boxSizing = 'border-box';
    element.style.color = 'rgb(42, 42, 42)';
    element.style.fontFamily = 'Montserrat, Arial, sans-serif';
    element.style.fontSize = '28px';
    element.style.fontWeight = '400';
    element.style.height = '52px';
    element.style.lineHeight = '24px';
    element.style.margin = '0px';
    element.style.padding = '13px';
    return (element);
  }

  // ----------------------------------------------------------------------------------------------
  private submit(): void {
    if (!this.nameInput.checkValidity()) {
      this.txtNameStatus.error = this.nameInput.validationMessage;
      return;
    }

    if (!this.emailInput.checkValidity()) {
      this.txtEmailStatus.error = this.emailInput.validationMessage;
      return;
    }

    const data: ISubmitFormData = {
      name: this.nameInput.value,
      email: this.emailInput.value,
    };

    this._events.emit(SubmitFormEvents.COMPLETE, data, this);
  }
}
